import React, { useEffect, useMemo, useState } from "react";
import { ordersApi, expensesApi, reportsApi } from "../api";
import SummaryShareModal from "./SummaryShareModal";
import { useI18n } from "../i18n";
import { getTimeframe, setTimeframe, getCurrentFYRange, getPreviousFYRange, toIsoRange } from "../timeframe";
import SubscriptionBanner from "./SubscriptionBanner";
import { useAccount } from "../contexts/AccountContext";

function toDateOnlyStr(d) {
  return new Date(d).toISOString().split("T")[0];
}
function toJsDateMaybe(tsOrDate) {
  if (tsOrDate && typeof tsOrDate === "object" && "_seconds" in tsOrDate) {
    return new Date(tsOrDate._seconds * 1000);
  }
  return new Date(tsOrDate || Date.now());
}

export default function Dashboard({ user, subStatus, onSubscribe, onOpenProfile, onGoToOrders }) {
  const { t } = useI18n();
  const { selectedAccountId, viewMode, setViewMode } = useAccount();
  const [orders, setOrders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [topProductInsight, setTopProductInsight] = useState(null);
  // Show trial upsell until user becomes Pro (annual + active)

  useEffect(() => {
    const start = Date.now();
    try { if (window && window.gtag) window.gtag('event', 'engagement_begin', { screen_name: 'dashboard' }); } catch {}
    return () => {
      try { if (window && window.gtag) window.gtag('event', 'engagement_time', { screen_name: 'dashboard', value: Date.now() - start }); } catch {}
    };
  }, []);

  const [timeframe, setTfState] = useState(() => getTimeframe());

  useEffect(() => {
    const handler = (e) => { setTfState(getTimeframe()); };
    window.addEventListener('TP_TIMEFRAME_CHANGE', handler);
    return () => window.removeEventListener('TP_TIMEFRAME_CHANGE', handler);
  }, []);


  useEffect(() => {
    (async () => {
      try {
        const iso = toIsoRange(timeframe);
        const params = {};
        if (iso.from && iso.to) {
          params.from = iso.from;
          params.to = iso.to;
        }
        if (viewMode === 'account' && selectedAccountId) {
          params.accountId = selectedAccountId;
        }
        
        const summaryPayload = {
          from: iso.from,
          to: iso.to,
          orders: {},
          expenses: {},
        };
        if (viewMode === 'account' && selectedAccountId) {
          summaryPayload.accountId = selectedAccountId;
        }

        const [orderData, expenseData, summaryData] = await Promise.all([
          ordersApi.list(params),
          expensesApi.list(params).catch(() => []),
          reportsApi.summary(summaryPayload).catch(() => null),
        ]);
        const mapped = (orderData || []).map((o) => ({
          name: o.name || '',
          date: toJsDateMaybe(o.date),
          sellingPrice: Number(o.sellingPrice) || 0,
          totalCost: Number(o.totalCost) || 0,
        }));
        setOrders(mapped);
        const mappedExp = (expenseData || []).map((e) => ({
          date: toJsDateMaybe(e.date),
          amount: Number(e.amount) || 0,
          frequency: (e.frequency || 'daily').toLowerCase(),
        }));
        setExpenses(mappedExp);
        setTopProductInsight(summaryData?.orders?.topProducts?.[0] || null);
      } catch (e) {
        console.error("Failed to load dashboard orders", e);
        setTopProductInsight(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [timeframe, selectedAccountId, viewMode]);

  const todayStr = toDateOnlyStr(new Date());

  const metrics = useMemo(() => {
    const profit = (o) => (o.sellingPrice || 0) - (o.totalCost || 0);

    // Range from global timeframe
    const from = new Date(timeframe.from);
    const to = new Date(timeframe.to);
    to.setHours(23,59,59,999);
    const within = (d) => {
      const dt = new Date(d);
      return dt >= from && dt <= to;
    };

    const fyOrders = orders.filter((o) => within(o.date));
    const fyExpensesDaily = expenses.filter((e) => e.frequency === 'daily' && within(e.date));
    const fyExpensesMonthly = expenses.filter((e) => e.frequency === 'monthly' && within(e.date));

    const ordersProfit = fyOrders.reduce((s, o) => s + profit(o), 0);
    const allExpenses = [...fyExpensesDaily, ...fyExpensesMonthly].reduce((s, e) => s + (e.amount || 0), 0);
    const totalProfit = ordersProfit - allExpenses;
    const totalOrders = fyOrders.length;

    const todays = fyOrders.filter((o) => toDateOnlyStr(o.date) === todayStr);
    const todaysExpenses = [...fyExpensesDaily]
      .filter((e) => e.frequency === 'daily' && toDateOnlyStr(e.date) === todayStr)
      .reduce((s,e)=> s + (e.amount || 0), 0);
    const todaysProfit = todays.reduce((s, o) => s + profit(o), 0) - todaysExpenses;
    const todaysSales = todays.reduce((s, o) => s + (o.sellingPrice || 0), 0);
    const todaysCost = todays.reduce((s, o) => s + (o.totalCost || 0), 0);

    const days = [];
    const todayWeek = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(todayWeek);
      d.setDate(todayWeek.getDate() - i);
      const key = toDateOnlyStr(d);
      days.push(key);
    }
    const byDay = Object.fromEntries(days.map((d) => [d, 0]));
    for (const o of fyOrders) {
      const key = toDateOnlyStr(o.date);
      if (byDay[key] !== undefined) byDay[key] += profit(o);
    }
    for (const e of fyExpensesDaily) {
      const key = toDateOnlyStr(e.date);
      if (byDay[key] !== undefined) byDay[key] -= (e.amount || 0);
    }
    const weekSeries = days.map((d) => byDay[d] || 0);
    const maxVal = Math.max(1, ...weekSeries.map(Math.abs));

    return { totalProfit, totalOrders, todaysProfit, todaysSales, todaysCost, todaysOrders: todays, weekSeries, maxVal };
  }, [orders, expenses, todayStr, timeframe]);

  const userName = user?.name || user?.userName || 'there';
  const topProductName = topProductInsight?.name || t('dash.topProduct.empty.title');
  const topProductRevenue = topProductInsight
    ? `₹${Number(topProductInsight.revenue || 0).toLocaleString('en-IN')}`
    : null;
  const shareSuffix = t('dash.topProduct.shareSuffix', 'of revenue');
  const topProductShare = topProductInsight && typeof topProductInsight.share === 'number'
    ? `${Math.round(topProductInsight.share * 1000) / 10}% ${shareSuffix}`
    : t('dash.topProduct.empty.desc');
  const topProductInitials = (topProductInsight?.name || '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2) || 'TP';
  const showPaywall = subStatus && subStatus.valid === false;
  const pending = !!subStatus?.requestPending;
  const now = new Date();
  const year = now.getFullYear();
  const prevMonthDate = new Date(year, now.getMonth() - 1, 1);
  const currMonShort = now.toLocaleString('en-US', { month: 'short' });
  const prevMonShort = prevMonthDate.getFullYear() === year
    ? prevMonthDate.toLocaleString('en-US', { month: 'short' })
    : '';

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-lg font-bold mb-1">{t('dash.greeting').replace('{name}', userName)}</div>
          <div className="text-sm text-gray-600">{t('dash.subtitle')}</div>
        </div>
        <div />
      </div>


      {showPaywall && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4">
          <div className="text-sm font-semibold text-yellow-800">{t('dash.paywall.title')}</div>
          <div className="text-xs text-yellow-700 mt-1">{t('dash.paywall.desc')}</div>
          {subStatus?.quotaInfo && (
            <div className="text-xs text-yellow-600 mt-1">
              <div>Usage: {subStatus.quotaInfo.combined?.usage || (subStatus.quotaInfo.orders.usage + subStatus.quotaInfo.expenses.usage)} / {subStatus.quotaInfo.combined?.limit || subStatus.quotaInfo.orders.limit} total entries</div>
            </div>
          )}
          <button disabled={pending} onClick={async ()=>{ const msg = await onSubscribe?.(); if (msg) setInfo(msg); }} className={`mt-3 text-white text-sm px-3 py-1.5 rounded ${pending ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600'}`}>{pending ? t('dash.paywall.pending') : t('dash.paywall.subscribe')}</button>
          {(pending || info) && <div className="text-xs text-green-700 mt-2">{info || t('dash.paywall.info')}</div>}
        </div>
      )}

      {/* Gentle trial upsell card: shown until user becomes active annual */}
      {(() => {
        const isPro = !!(subStatus?.valid) && subStatus?.plan !== 'trial';
        return !isPro;
      })() && (
        <div className="bg-green-50 border border-green-100 rounded-2xl p-3 mb-4 shadow-sm flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-green-800">{t('dash.trial.title')}</div>
            <div className="text-xs text-gray-700 mt-0.5">{t('dash.trial.subtitle')}</div>
            {subStatus?.quotaInfo && (
              <div className="text-xs text-gray-600 mt-1">
                <div>Usage: {subStatus.quotaInfo.combined?.usage || (subStatus.quotaInfo.orders.usage + subStatus.quotaInfo.expenses.usage)} / {subStatus.quotaInfo.combined?.limit || subStatus.quotaInfo.orders.limit} total entries</div>
              </div>
            )}
            {(pending || info) && (
              <div className="text-xs text-green-700 mt-2">{info || t('dash.paywall.info')}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={pending}
              onClick={async ()=>{ const msg = await onSubscribe?.(); if (msg) setInfo(msg); }}
              className={`text-xs px-3 py-1.5 rounded ${pending ? 'bg-green-300 cursor-not-allowed text-white' : 'bg-green-600 text-white'}`}
            >
              {pending ? t('dash.paywall.pending') : t('dash.trial.cta')}
            </button>
          </div>
        </div>
      )}

      {/* Timeframe selector and View Mode toggle */}
      <div className="mb-3 space-y-3">
        <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <div className="sm:col-span-2">
              <div className="text-[11px] text-gray-600 mb-1">Timeframe</div>
              <div className="flex items-center gap-2">
                <button className={`text-xs px-3 py-1.5 rounded-full border ${timeframe.mode==='fy_current' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-200'}`} onClick={()=>{ const tf = { mode: 'fy_current', ...getCurrentFYRange() }; setTimeframe(tf); setTfState(tf); }}>Current FY</button>
                <button className={`text-xs px-3 py-1.5 rounded-full border ${timeframe.mode==='fy_previous' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-200'}`} onClick={()=>{ const tf = { mode: 'fy_previous', ...getPreviousFYRange() }; setTimeframe(tf); setTfState(tf); }}>Previous FY</button>
                <button className={`text-xs px-3 py-1.5 rounded-full border ${timeframe.mode==='custom' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-200'}`} onClick={()=>{ const tf = { mode: 'custom', from: timeframe.from, to: timeframe.to }; setTimeframe(tf); setTfState(tf); }}>Custom</button>
              </div>
            </div>
            <div className="sm:col-span-1">
              <div className="grid grid-cols-2 gap-2">
                <input type="date" className="border rounded px-2 py-1 text-sm" value={timeframe.from} onChange={(e)=>{ const tf = { ...timeframe, mode: 'custom', from: e.target.value || timeframe.from }; setTimeframe(tf); setTfState(tf); }} />
                <input type="date" className="border rounded px-2 py-1 text-sm" value={timeframe.to} onChange={(e)=>{ const tf = { ...timeframe, mode: 'custom', to: e.target.value || timeframe.to }; setTimeframe(tf); setTfState(tf); }} />
              </div>
            </div>
          </div>
        </div>
        
        {/* View Mode Toggle */}
        <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] text-gray-600 mb-1">View Mode</div>
              <div className="text-sm font-medium text-gray-800">
                {viewMode === 'account' ? 'Business Account View' : 'Organization View'}
              </div>
              <div className="text-xs text-gray-500">
                {viewMode === 'account' 
                  ? 'Showing data for selected business account only' 
                  : 'Showing data for all business accounts in your organization'
                }
              </div>
            </div>
            <button
              onClick={() => {
                const newViewMode = viewMode === 'account' ? 'org' : 'account';
                setViewMode(newViewMode);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                viewMode === 'org' ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  viewMode === 'org' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className={`rounded-xl p-3 text-center shadow-sm border ${
          metrics.todaysProfit < 0 
            ? 'bg-red-50 border-red-200' 
            : 'bg-green-50 border-green-100'
        }`}>
          <div className="text-xs text-gray-500 mb-1">{t('dash.card.todaysProfit')}</div>
          <div className={`text-sm sm:text-lg lg:text-xl font-bold break-words overflow-hidden ${
            metrics.todaysProfit < 0 ? 'text-red-700' : 'text-green-700'
          }`}>
            {loading ? "…" : `₹${Number(metrics.todaysProfit).toLocaleString('en-IN')}`}
          </div>
        </div>
        <div className={`rounded-xl p-3 text-center shadow-sm border ${
          metrics.totalProfit < 0 
            ? 'bg-red-50 border-red-200' 
            : 'bg-green-50 border-green-100'
        }`}>
          <div className="text-xs text-gray-500 mb-1">{t('dash.card.totalProfit')}</div>
          <div className={`text-sm sm:text-lg lg:text-xl font-bold break-words overflow-hidden ${
            metrics.totalProfit < 0 ? 'text-red-700' : 'text-green-700'
          }`}>
            {loading ? "…" : `₹${Number(metrics.totalProfit).toLocaleString('en-IN')}`}
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center shadow-sm border border-green-100">
          <div className="text-xs text-gray-500 mb-1">{t('dash.card.totalOrders')}</div>
          <div className="text-sm sm:text-lg lg:text-xl font-bold text-green-700 break-words overflow-hidden">
            {loading ? "…" : Number(metrics.totalOrders).toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      <div className="bg-green-50 rounded-2xl p-4 mb-4 border border-green-100">
        <div className="text-sm font-semibold mb-2">{t('dash.section.weekProfit')}</div>
        <WeekChart series={metrics.weekSeries} maxVal={metrics.maxVal} />
      </div>

      <div className="bg-white border border-green-100 rounded-2xl p-4 mb-4 shadow-sm">
        <div className="text-sm font-semibold mb-2">{t('dash.section.topProduct')}</div>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center text-green-700 font-semibold">
            {topProductInitials}
          </div>
          <div className="flex-1">
            <div className="font-medium text-gray-900 break-words">{topProductName}</div>
            {topProductRevenue ? (
              <div className="text-xs text-gray-600 mt-0.5">
                <span className="font-semibold text-green-700">{topProductRevenue}</span>
                <span className="ml-2 text-gray-500">{topProductShare}</span>
              </div>
            ) : (
              <div className="text-xs text-gray-500">{topProductShare}</div>
            )}
          </div>
        </div>
      </div>

      <button className="w-full bg-white border border-green-100 rounded-2xl py-2.5 mb-20 flex items-center justify-center gap-2 text-sm shadow-sm" onClick={() => setShowSummary(true)}>
        <span className="text-green-700">📤</span>
        <span className="font-medium text-gray-700">Share Weekly Summary</span>
      </button>

      <button
        className="fixed bottom-24 right-6 z-20 bg-green-500 text-white w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg text-2xl sm:text-3xl flex items-center justify-center leading-none"
        onClick={() => onGoToOrders?.()}
      >
        <span className="pointer-events-none translate-y-[1px]">+</span>
      </button>

      {(() => {
        const end = new Date();
        const start = new Date(end);
        start.setDate(end.getDate() - 6);
        const toKey = toDateOnlyStr(end);
        const fromKey = toDateOnlyStr(start);
        const keys = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          keys.push(toDateOnlyStr(d));
        }
        const isDailyExpense = (e) => String(e.frequency || 'daily').toLowerCase() === 'daily';
        const byDay = keys.map((k) => {
          const dayOrders = (orders || []).filter(o => toDateOnlyStr(o.date) === k);
          const sales = dayOrders.reduce((s, o) => s + (o.sellingPrice || 0), 0);
          const orderCost = dayOrders.reduce((s, o) => s + (o.totalCost || 0), 0);
          const expensesDaily = (expenses || []).filter(e => isDailyExpense(e) && toDateOnlyStr(e.date) === k).reduce((s, e) => s + (e.amount || 0), 0);
          const profit = sales - orderCost - expensesDaily;
          return { date: k, sales, orderCost, expensesDaily, profit };
        });
        const totals = byDay.reduce((acc, d) => ({
          sales: acc.sales + d.sales,
          orderCost: acc.orderCost + d.orderCost,
          expensesDaily: acc.expensesDaily + d.expensesDaily,
          profit: acc.profit + d.profit,
        }), { sales: 0, orderCost: 0, expensesDaily: 0, profit: 0 });
        return (
          <SummaryShareModal
            open={showSummary}
            onClose={() => setShowSummary(false)}
            data={{
              date: `${fromKey} → ${toKey}`,
              timeframe: 'week',
              dayWise: byDay,
              totals,
            }}
          />
        );
      })()}
    </div>
  );
}

function WeekChart({ series, maxVal }) {
  const [active, setActive] = React.useState(null);
  const [hoverIdx, setHoverIdx] = React.useState(null);
  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  return (
    <div className="relative">
      <div className="h-24 bg-white rounded-lg shadow-inner flex items-end gap-1 p-2">
        {series.map((val, idx) => {
          const pct = Math.max(5, Math.round((Math.abs(val) / Math.max(1, Math.abs(maxVal))) * 100));
          const isNegative = val < 0;
          const shades = isNegative 
            ? ["bg-red-100","bg-red-200","bg-red-300","bg-red-400"]
            : ["bg-green-100","bg-green-200","bg-green-300","bg-green-400"];
          const shade = shades[Math.min(shades.length - 1, Math.floor((pct / 100) * shades.length))];
          const isActive = active === idx || hoverIdx === idx;
          return (
            <button
              key={idx}
              type="button"
              className={`flex-1 rounded-sm ${shade} relative focus:outline-none`} style={{ height: `${pct}%` }}
              onClick={() => setActive(active === idx ? null : idx)}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx(null)}
              aria-label={fmt(val)}
              title={fmt(val)}
            >
              {isActive && (
                <div className={`absolute -top-6 left-1/2 -translate-x-1/2 text-white text-[10px] px-2 py-0.5 rounded shadow ${
                  isNegative ? 'bg-red-700' : 'bg-green-700'
                }`}>
                  {fmt(val)}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}